/**
 * Room: the meeting view.
 *
 * Everything else in this workbench is a professional tool at reading
 * distance. This is the one screen read from across a table, often projected,
 * by people who are also talking — so the type is larger, the chrome is gone,
 * and each row says one thing.
 *
 * Two jobs, and they are the two things that happen in a meeting and nowhere
 * else.
 *
 * A decision gets logged while the room is still in it. The agenda item
 * supplies the subject, so the only thing anyone types is what was decided,
 * and Enter files it — a decision written up afterwards is a decision written
 * from memory, and the ledger's whole worth is that it is not.
 *
 * A straw poll gets a truer ranking than a show of hands, but only while the
 * anonymity holds. So this screen shows turnout while a poll is open and never
 * a running tally: a count that moves as people vote is a count that attributes
 * them. It says that out loud rather than being quietly careful, because a
 * group that does not know why it is safe will not use it as though it is.
 *
 * The larger register is the one thing here that departs from the rest of the
 * workbench, and it stops at the type. Everything else — the 28px screen
 * padding, the 6px button radius, the strong rule at a section boundary, the
 * mono micro-label naming each block — is the same vocabulary Ask uses, because
 * "read across a table" is a reason for 19px body text and not a reason for a
 * second design. The micro-labels sit at 12px rather than 9: still inside the
 * 9–12px the spec sets for mono, and legible from the back of a staffroom. They
 * were at 13, which is outside it, and carried a redundant colour override —
 * `.label` is already muted.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type RoomData } from '../lib/api.js';

const TYPE_LABEL: Record<string, string> = {
  protect: 'Protect',
  improve: 'Improve',
  new: 'New',
  correct: 'Correct',
};

function Agenda({
  data,
  isRep,
  onOpenClaim,
}: {
  data: RoomData;
  isRep: boolean;
  onOpenClaim: (ref: string) => void;
}) {
  const queryClient = useQueryClient();
  const [logging, setLogging] = useState<string | null>(null);
  const [resolution, setResolution] = useState('');

  const decide = useMutation({
    mutationFn: (body: { subject: string; resolution: string }) => api.logDecision(body),
    onSuccess: () => {
      setLogging(null);
      setResolution('');
      void queryClient.invalidateQueries({ queryKey: ['room'] });
    },
  });

  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="label text-[12px]">On the table</h2>
        <span className="label text-[12px]">{data.agenda.length}</span>
      </div>
      <ul>
        {data.agenda.map((item) => (
          // The hairline belongs to the row, so the last one drops it and the
          // section's own strong rule is the only line at the boundary. Two
          // rules 24px apart read as an empty band.
          <li key={item.ref} className="border-b border-[var(--color-rule)] py-3 last:border-b-0">
            <div className="flex items-baseline gap-3">
              <button
                type="button"
                onClick={() => onOpenClaim(item.ref)}
                className="clause-number shrink-0 text-[15px] underline decoration-[var(--color-rule-strong)] underline-offset-4"
              >
                {item.ref}
              </button>
              <span className="min-w-0 flex-1 text-[19px] leading-snug">{item.title}</span>
              <span className="label shrink-0 text-[12px]">
                {TYPE_LABEL[item.type] ?? item.type}
                {item.redTeamItems > 0 && (
                  /*
                    Not the defect colour. An objection on a claim is the
                    group's own work, not a fault in it — the red said "this
                    row is broken" about a claim that had been stress-tested,
                    which is the opposite of the truth. It is a count, so it
                    reads as one.
                  */
                  <span className="ml-2 text-[var(--color-ink)]">
                    {item.redTeamItems} objection{item.redTeamItems === 1 ? '' : 's'}
                  </span>
                )}
              </span>
              {isRep && logging !== item.ref && (
                <button
                  type="button"
                  onClick={() => {
                    setLogging(item.ref);
                    setResolution('');
                  }}
                  className="label hit shrink-0 text-[12px] underline underline-offset-4"
                >
                  Decided…
                </button>
              )}
            </div>

            {logging === item.ref && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (resolution.trim().length < 3) return;
                  decide.mutate({
                    subject: `${item.ref} — ${item.title}`,
                    resolution: resolution.trim(),
                  });
                }}
                className="mt-2 flex gap-2"
              >
                {/*
                  The subject is the agenda item, so the only thing typed here
                  is what was decided, and Enter files it. Anything more is a
                  form somebody fills in later from memory.
                */}
                <label htmlFor={`decide-${item.ref}`} className="sr-only">
                  What was decided about {item.ref}
                </label>
                <input
                  id={`decide-${item.ref}`}
                  autoFocus
                  value={resolution}
                  onChange={(event) => setResolution(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setLogging(null);
                  }}
                  placeholder="What was decided?"
                  className="hit min-w-0 flex-1 rounded-[6px] border border-[var(--color-ink)] px-3 py-2 text-[17px]"
                />
                {/* Ink ground, as Ask sets its Ask button: the one control on
                    the row that commits something the group has to live with. */}
                <button
                  type="submit"
                  disabled={decide.isPending || resolution.trim().length < 3}
                  className="hit shrink-0 rounded-[6px] px-4 py-2 text-[15px] disabled:opacity-40"
                  style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
                >
                  Log it
                </button>
              </form>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Polls({ data, isRep }: { data: RoomData; isRep: boolean }) {
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState('');
  const [voted, setVoted] = useState<Record<string, string>>({});
  const [shown, setShown] = useState<string | null>(null);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['room'] });
  };

  const open = useMutation({
    mutationFn: api.openPoll,
    onSuccess: () => {
      setOpening(false);
      setQuestion('');
      setOptions('');
      refresh();
    },
  });
  const vote = useMutation({
    mutationFn: ({ id, response }: { id: string; response: string }) => api.vote(id, response),
    onSuccess: (_r, variables) => {
      setVoted((prev) => ({ ...prev, [variables.id]: variables.response }));
      refresh();
    },
    onError: (error: Error, variables) => {
      // "You have already answered this one" is the unique constraint talking,
      // and it is information the voter wants rather than a failure.
      setVoted((prev) => ({ ...prev, [variables.id]: error.message }));
    },
  });
  const close = useMutation({ mutationFn: api.closePoll, onSuccess: refresh });

  const result = useQuery({
    queryKey: ['pollResult', shown],
    queryFn: () => api.pollResult(shown!),
    enabled: Boolean(shown),
  });

  return (
    <section className="section-rule mt-8 pt-6">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="label text-[12px]">Straw polls</h2>
        {isRep && !opening && (
          <button
            type="button"
            onClick={() => setOpening(true)}
            className="label hit text-[12px] underline underline-offset-4"
          >
            Ask the room
          </button>
        )}
      </div>

      {opening && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const list = options
              .split('\n')
              .map((o) => o.trim())
              .filter(Boolean);
            if (question.trim().length < 5 || list.length < 2) return;
            open.mutate({ question: question.trim(), kind: 'single', options: list });
          }}
          className="card mb-4"
        >
          <div className="card-header">
            <p className="label m-0 text-[12px]">Ask the room</p>
            <span className="label text-[12px]">nobody sees how anyone answered</span>
          </div>
          <label htmlFor="poll-question" className="sr-only">
            The question to put to the room
          </label>
          <input
            id="poll-question"
            autoFocus
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="What should the room decide?"
            className="hit mb-2 w-full rounded-[6px] border border-[var(--color-ink)] px-3 py-2 text-[17px]"
          />
          <label htmlFor="poll-options" className="sr-only">
            The options, one per line
          </label>
          <textarea
            id="poll-options"
            value={options}
            onChange={(event) => setOptions(event.target.value)}
            placeholder={'One option per line\nAt least two'}
            rows={3}
            className="mb-2 w-full rounded-[6px] border border-[var(--color-rule-strong)] px-3 py-2 text-[15px]"
          />
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={open.isPending}
              className="hit rounded-[6px] px-4 py-2 text-[15px] disabled:opacity-40"
              style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
            >
              Open it
            </button>
            <button
              type="button"
              onClick={() => setOpening(false)}
              className="label hit text-[12px] underline underline-offset-4"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {data.polls.length === 0 && !opening && (
        <p className="measure-wide text-[15px] leading-relaxed text-[var(--color-muted)]">
          Nothing asked yet. A ballot gets a truer ranking than a show of hands, because nobody has
          to be the one who says it first.
        </p>
      )}

      {data.polls.map((poll) => (
        <article
          key={poll.id}
          className="mb-4 border-b border-[var(--color-rule)] pb-4 last:mb-0 last:border-b-0 last:pb-0"
        >
          <p className="mb-2 text-[19px] leading-snug">{poll.question}</p>

          {!poll.closed && (
            <>
              <div className="mb-2 flex flex-wrap gap-2">
                {poll.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={Boolean(voted[poll.id]) || vote.isPending}
                    onClick={() => vote.mutate({ id: poll.id, response: option })}
                    className="hit rounded-[6px] border border-[var(--color-ink)] px-4 py-2 text-[17px] disabled:opacity-40"
                  >
                    {option}
                  </button>
                ))}
              </div>
              {voted[poll.id] && (
                <p role="status" className="mb-1 text-[15px] text-[var(--color-muted)]">
                  {voted[poll.id]}
                </p>
              )}
              {/*
                Turnout, never a tally. A count that moves as people vote lets
                anyone watching match a change to the person who just looked at
                their phone, which is the whole anonymity gone.
              */}
              <p className="measure-wide text-[15px] leading-relaxed text-[var(--color-muted)]">
                {poll.turnout} answered so far. How they answered stays hidden until this closes.
              </p>
              {isRep && (
                <button
                  type="button"
                  onClick={() => close.mutate(poll.id)}
                  className="label hit mt-2 text-[12px] underline underline-offset-4"
                >
                  Close and show the result
                </button>
              )}
            </>
          )}

          {poll.closed && (
            <>
              <button
                type="button"
                onClick={() => setShown(shown === poll.id ? null : poll.id)}
                aria-expanded={shown === poll.id}
                className="label hit text-[12px] underline underline-offset-4"
              >
                {shown === poll.id ? 'Hide the result' : 'Show the result'}
              </button>

              {shown === poll.id && result.data && (
                // Announced, because a result appearing after a click is the
                // moment the room is waiting on.
                <div className="mt-2" aria-live="polite">
                  {result.data.withheld ? (
                    /*
                      Withholding a result is the anonymity guarantee holding,
                      not a fault, so it gets the same neutral frame Ask gives a
                      refusal rather than the defect colour — which in a room of
                      people watching a projector reads as the poll having gone
                      wrong.
                    */
                    <div className="measure-wide rounded-[8px] border border-[var(--color-rule-strong)] px-3 py-2.5">
                      <p className="label mb-1 text-[12px]">Result withheld</p>
                      <p className="text-[15px] leading-relaxed">{result.data.withheld}</p>
                    </div>
                  ) : (
                    <ul className="mb-2">
                      {result.data.results.map((row) => (
                        <li key={row.option} className="flex items-baseline gap-3 text-[19px]">
                          <span className="clause-number w-10 shrink-0 text-right">{row.votes}</span>
                          <span>{row.option}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Said on the result, where someone decides how candid to be next time. */}
                  <p className="measure-wide mt-2 text-[13px] leading-relaxed text-[var(--color-muted)]">
                    {result.data.caveat}
                  </p>
                </div>
              )}
            </>
          )}
        </article>
      ))}
    </section>
  );
}

export function Room({
  isRep,
  onOpenClaim,
}: {
  isRep: boolean;
  onOpenClaim: (ref: string) => void;
}) {
  const room = useQuery({ queryKey: ['room'], queryFn: api.room });
  const data = room.data;

  if (!data) return <p className="screen text-[17px] text-[var(--color-muted)]">Loading…</p>;

  return (
    /*
      The screen's own 28px padding, and the wide column. `.screen-column` caps
      at 760px, which is a reading measure for one person at arm's length; this
      is read at 19px from the far side of a table, where that measure wraps
      every claim title onto two lines.
    */
    <div className="screen">
      <div className="screen-column-wide">
        <Agenda data={data} isRep={isRep} onOpenClaim={onOpenClaim} />
        <Polls data={data} isRep={isRep} />

        <section className="section-rule mt-8 pt-6">
          <h2 className="label mb-3 text-[12px]">Decided lately</h2>
          {data.recentDecisions.length === 0 ? (
            <p className="text-[15px] text-[var(--color-muted)]">Nothing logged yet.</p>
          ) : (
            <ul>
              {data.recentDecisions.map((decision) => (
                <li key={decision.id} className="border-b border-[var(--color-rule)] py-2.5 last:border-b-0">
                  <p className="text-[17px] leading-snug">{decision.resolution}</p>
                  <p className="label mt-1 text-[12px]">
                    {decision.subject} · {decision.occurredOn}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
